// ─── Lens-Aware Copy ─────────────────────────────────────────────────────────
//
// Centralized patient vs clinician string variants. Keeping all variants in
// one file prevents drift between the two tones and makes content review +
// future i18n scaffolding trivial.
//
// Tone guidance:
//   - patient: supportive, non-diagnostic, plain language, second person
//   - clinician: concise, clinical register, third person or impersonal
//
// Helper functions take raw engine inputs (or primitives) and return a
// `LensCopy` object. All functions are pure and unit tested.
// ──────────────────────────────────────────────────────────────────────────────

import type { LensCopy, ConfidenceLevel, SeverityBand, TrendDirection } from './types';

// ─── Generic helpers ─────────────────────────────────────────────────────────

/** Create a LensCopy from two strings. */
export function lensCopy(patient: string, clinician: string): LensCopy {
  return { patient, clinician };
}

/** Select one side of a LensCopy. Prefer this over destructuring in components. */
export function pickLens(copy: LensCopy, lens: 'patient' | 'clinician'): string {
  return copy[lens];
}

// ─── Confidence labels ───────────────────────────────────────────────────────

const CONFIDENCE_LABELS: Record<ConfidenceLevel, LensCopy> = {
  high: lensCopy(
    'Strong signal across your data.',
    'High confidence — multiple concordant signals.',
  ),
  moderate: lensCopy(
    'A reasonable pattern in your recent data.',
    'Moderate confidence — supporting signals present.',
  ),
  low: lensCopy(
    'Early signal — worth keeping an eye on.',
    'Low confidence — limited supporting data.',
  ),
  insufficient: lensCopy(
    "We don't have enough data yet to say.",
    'Insufficient data — interpretation not supported.',
  ),
};

export function confidenceLabel(level: ConfidenceLevel): LensCopy {
  return CONFIDENCE_LABELS[level];
}

// ─── Primary insight headlines (by domain) ───────────────────────────────────

/**
 * Generate a lens-aware headline for the primary insight based on the leading
 * domain and its severity. Kept minimal on purpose — richer phrasing lives in
 * the explanation field or pulls from phenotypeInterpretations.
 *
 * Patient headline uses pillarDisplayName() so keys like "phq4" become
 * "Mood and anxiety check-ins" — never raw screener names.
 * Clinician headline keeps a concise interpretive register.
 *
 * Mood domains use a different template structure because their display
 * name ("Mood and anxiety check-ins") is plural — generic templates like
 * "X needs your attention" produce subject-verb disagreement.
 */
export function primaryInsightHeadline(
  domain: string,
  severity: SeverityBand,
): LensCopy {
  const isMood = isMoodDomain(domain);
  const patientName = pillarDisplayName(domain);
  const clinicianName = patientName;

  if (severity === 'attention') {
    if (isMood) {
      return lensCopy(
        'Your mood has been asking for some care',
        'Mood screening flagged for review',
      );
    }
    return lensCopy(
      `${patientName} needs your attention`,
      `${clinicianName} flagged for review`,
    );
  }
  if (severity === 'fair') {
    if (isMood) {
      return lensCopy(
        'Your mood has been running a bit heavier',
        'Mood screening running above typical range',
      );
    }
    return lensCopy(
      `${patientName} is feeling strained`,
      `${clinicianName} below target`,
    );
  }
  if (severity === 'good') {
    if (isMood) {
      return lensCopy(
        'Your mood is tracking well',
        'Mood screening within target range',
      );
    }
    return lensCopy(
      `${patientName} is tracking well`,
      `${clinicianName} within target range`,
    );
  }
  if (severity === 'excellent') {
    if (isMood) {
      return lensCopy(
        'Your mood is in a strong place',
        'Mood screening optimal',
      );
    }
    return lensCopy(
      `${patientName} is in a strong place`,
      `${clinicianName} optimal`,
    );
  }
  if (isMood) {
    return lensCopy(
      'We need more check-in data to share a mood insight',
      'Mood — insufficient data',
    );
  }
  return lensCopy(
    `We need more data for ${patientName.toLowerCase()}`,
    `${clinicianName} — insufficient data`,
  );
}

// ─── Next step suggestions (by domain) ───────────────────────────────────────

const NEXT_STEPS_BY_DOMAIN: Record<string, Record<SeverityBand, LensCopy>> = {
  sleep: {
    excellent: lensCopy('Keep your current bedtime rhythm.', 'Maintain current sleep routine.'),
    good: lensCopy('Keep your current bedtime rhythm.', 'Maintain current sleep routine.'),
    fair: lensCopy(
      'Try winding down 30 minutes earlier for a few nights.',
      'Consider counseling on earlier sleep onset and reduced evening arousal.',
    ),
    attention: lensCopy(
      'Prioritize sleep this week — earlier bedtime, calmer evenings.',
      'Address sleep consolidation; assess for stressors, caffeine, late exercise.',
    ),
    no_data: lensCopy(
      'Wear your ring or watch overnight to see sleep insights.',
      'No wearable sleep data available this period.',
    ),
  },
  movement: {
    excellent: lensCopy('Your movement routine is doing its job.', 'Physical activity within target.'),
    good: lensCopy('Your movement routine is doing its job.', 'Physical activity within target.'),
    fair: lensCopy(
      'Aim for a short daily walk — even 10 minutes helps.',
      'Consider behavioral activation; short-bout activity prescription.',
    ),
    attention: lensCopy(
      'Start small — one short walk today counts.',
      'Consider structured activation; assess for barriers to mobility or mood.',
    ),
    no_data: lensCopy(
      'Connect a step counter to see movement insights.',
      'No activity data available this period.',
    ),
  },
  stress: {
    excellent: lensCopy('Your recovery signals look resilient.', 'Autonomic recovery within target.'),
    good: lensCopy('Your recovery signals look resilient.', 'Autonomic recovery within target.'),
    fair: lensCopy(
      'Try a few minutes of slow breathing or outdoor time today.',
      'Consider stress physiology and autonomic recovery strategies.',
    ),
    attention: lensCopy(
      'Give yourself permission to slow down today.',
      'Assess for chronic stress load; consider HPA-axis review.',
    ),
    no_data: lensCopy(
      'Daily HRV readings unlock stress insights.',
      'No HRV or recovery data available this period.',
    ),
  },
  nutrition: {
    excellent: lensCopy('Your nutrition signals look supportive.', 'Nutritional markers within target.'),
    good: lensCopy('Your nutrition signals look supportive.', 'Nutritional markers within target.'),
    fair: lensCopy(
      'A steady meal pattern and extra veggies can go a long way.',
      'Consider dietary quality assessment and micronutrient review.',
    ),
    attention: lensCopy(
      'Check in with your clinician about recent lab markers.',
      'Review micronutrient status and metabolic markers.',
    ),
    no_data: lensCopy(
      'Recent labs will unlock nutrition insights.',
      'No recent lab or dietary check-in data.',
    ),
  },
  social: {
    excellent: lensCopy('Your social connections look supportive.', 'Social engagement within target.'),
    good: lensCopy('Your social connections look supportive.', 'Social engagement within target.'),
    fair: lensCopy(
      'A short call or visit with someone who lifts you up can help.',
      'Consider behavioral activation around social engagement.',
    ),
    attention: lensCopy(
      "Reach out to one person today — even a brief hello counts.",
      'Address social withdrawal; assess for isolation risk.',
    ),
    no_data: lensCopy(
      'Your weekly check-in unlocks social insights.',
      'No recent self-report on social engagement.',
    ),
  },
  substance: {
    excellent: lensCopy('Low substance load — supportive for recovery.', 'Substance use within target.'),
    good: lensCopy('Low substance load — supportive for recovery.', 'Substance use within target.'),
    fair: lensCopy(
      'Consider lighter or earlier use if you can.',
      'Consider substance use review; assess impact on sleep and mood.',
    ),
    attention: lensCopy(
      'Talk with your clinician about your current use.',
      'Assess current substance use pattern and downstream impact.',
    ),
    no_data: lensCopy(
      'Your weekly check-in unlocks this insight.',
      'No recent self-report on substance use.',
    ),
  },
};

/** Generic fallback when a domain isn't in the map above. */
const GENERIC_NEXT_STEP: Record<SeverityBand, LensCopy> = {
  excellent: lensCopy('Keep doing what you\u2019re doing.', 'Continue current management.'),
  good: lensCopy('Keep doing what you\u2019re doing.', 'Continue current management.'),
  fair: lensCopy('Small consistent actions this week can help.', 'Continue monitoring.'),
  attention: lensCopy('Talk with your clinician about this.', 'Flag for clinical review.'),
  no_data: lensCopy('Add more data to see suggestions here.', 'Insufficient data for guidance.'),
};

export function nextStepForDomain(domain: string, severity: SeverityBand): LensCopy {
  const byDomain = NEXT_STEPS_BY_DOMAIN[domain];
  if (byDomain) return byDomain[severity];
  return GENERIC_NEXT_STEP[severity];
}

// ─── Patient-lens explanation translators ───────────────────────────────────
//
// The clinical engines (exceptionDetector, insights, pillarScoring) produce
// terse clinical strings containing metric notation, threshold operators,
// and score arrays. Those strings are fine for the clinician lens but must
// NEVER reach the patient lens.
//
// These translators take structured fields (pillar key, direction, category,
// severity band) and return plain-language sentences built from templates —
// not by parsing clinical strings. Keeping translation here means JSX never
// needs to know the difference.

/** Plain-language display name for a pillar/category key. */
export function pillarDisplayName(key: string): string {
  switch (key) {
    case 'sleep': return 'Sleep';
    case 'movement': return 'Movement';
    case 'nutrition': return 'Nutrition';
    case 'stress': return 'Stress and recovery';
    case 'social': return 'Social connection';
    case 'substance': return 'Substance use';
    case 'phq4': return 'Mood and anxiety check-ins';
    case 'hrv': return 'Your recovery signal';
    case 'mood': return 'Mood and anxiety check-ins';
    case 'lab': return 'Lab markers';
    case 'streak': return 'Your movement streak';
    case 'correlation': return 'A pattern we spotted';
    // exceptionDetector uses 'Mental Health' as the pillar label — alias it
    // to the same warm patient-facing name used for mood/phq4.
    case 'Mental Health': return 'Mood and anxiety check-ins';
    default: return key.charAt(0).toUpperCase() + key.slice(1);
  }
}

/**
 * True when a domain key represents the mood/mental-health system and
 * should use the warm check-in framing instead of the generic pillar name.
 */
export function isMoodDomain(key: string): boolean {
  return key === 'phq4' || key === 'mood' || key === 'Mental Health';
}

/** Direction-independent context for building a patient sentence. */
export interface PatientExplanationContext {
  /** Domain / pillar / category key. */
  domain: string;
  /** Severity band of the underlying data. */
  severity: SeverityBand;
  /** Optional trend direction. */
  trend?: TrendDirection;
}

/**
 * Runtime sanitizer for patient-facing strings + the plain-language
 * template builder below. The sanitizer matches the same patterns
 * enforced by patientLensSanitation.test.ts — if any forbidden pattern
 * is detected, the string is replaced with a safe fallback built from
 * the domain + severity + trend context via buildPatientExplanation.
 *
 * This is defense in depth. The correct long-term fix is to never
 * assign clinical strings to the patient side of a LensCopy in the
 * first place; this guard catches any such bug at runtime.
 */
const FORBIDDEN_PATIENT_PATTERNS: RegExp[] = [
  // Comparison operators with numbers (>= 6, < 80, etc.) — also catches
  // Unicode ≥ and ≤ which clinical engines sometimes emit.
  />=\s*\d|<=\s*\d|(?:^|[^A-Za-z])[><\u2265\u2264]\s*\d/,
  // Parenthesized numeric arrays like "(8, 7, 7)"
  /\(\s*\d+\s*,\s*\d+/,
  // Clinical screener names
  /\bPHQ-?\d|\bGAD-?\d/i,
  // Unexplained lab abbreviations (tolerates hyphen/space between letters
  // so "hs-CRP" and "hs CRP" are caught alongside "hsCRP").
  /\bhs[\s-]?CRP\b|\bHbA1c\b|\bTSH\b|\bTyG\b/i,
  // Raw NN/100 score notation
  /\b\d{1,3}\/100\b/,
];

/**
 * Check whether a string contains any forbidden clinical notation.
 * Pure function, no side effects.
 */
export function containsClinicalNotation(text: string): boolean {
  if (!text) return false;
  return FORBIDDEN_PATIENT_PATTERNS.some((re) => re.test(text));
}

/**
 * Guarantee a string is safe for the patient lens. If clinical notation
 * is detected, replace with a plain-language fallback built from the
 * given domain + severity + trend. Used as a final-pass sanitizer.
 */
export function sanitizePatientText(
  text: string,
  fallback: PatientExplanationContext,
): string {
  if (!containsClinicalNotation(text)) return text;
  return buildPatientExplanation(fallback);
}

export function buildPatientExplanation(ctx: PatientExplanationContext): string {
  const { domain, severity, trend } = ctx;
  const name = pillarDisplayName(domain);

  // Mood / mental-health gets a softer, lens-specific frame that avoids
  // clinical screener vocabulary and leans into everyday language.
  const isMood = isMoodDomain(domain);

  if (severity === 'attention') {
    if (isMood) {
      return 'Your recent mood and anxiety check-ins have been running a little heavier for a few weeks. It might be a good time to lean on support — talking with someone you trust, or your care team if that feels right.';
    }
    if (trend === 'declining') {
      return `${name} has been slipping lately. It might be worth giving this area a little more care this week.`;
    }
    return `${name} could use some care this week. Small, consistent actions can make a real difference.`;
  }

  if (severity === 'fair') {
    if (isMood) {
      return 'Your recent mood and anxiety check-ins have been running a bit higher than usual. Nothing alarming — just worth noticing as you go into this week.';
    }
    if (trend === 'declining') {
      return `${name} has been running a bit strained this week.`;
    }
    return `${name} is feeling a bit strained compared to your target range.`;
  }

  if (severity === 'good') {
    if (trend === 'improving') {
      return `${name} is trending in a supportive direction.`;
    }
    return `${name} is tracking well this period.`;
  }

  if (severity === 'excellent') {
    return `${name} is in a strong place right now.`;
  }

  return `We need a bit more ${name.toLowerCase()} data to share an insight here.`;
}

// ─── Clinician-lens explanation builder ─────────────────────────────────────
//
// Produces polished clinical prose for the clinician lens. Replaces the
// old pattern of concatenating raw engine strings (`critical.summary` +
// `clinicalNote`) which read like rule-engine output.
//
// The goal is interpretive, concise, professional phrasing — not
// alert-syntax. Still technical and precise, but written like a clinician
// would describe the signal to a colleague.

export interface ClinicianExplanationContext {
  domain: string;
  severity: SeverityBand;
  trend?: TrendDirection;
  /** Human-readable metric name from the engine (e.g. "PHQ-4 Sustained"). */
  metric?: string;
  /** Optional numeric current value for precision. */
  currentValue?: number | null;
  /** Optional duration phrase (e.g. "three consecutive weeks"). */
  durationPhrase?: string;
}

export function buildClinicianExplanation(ctx: ClinicianExplanationContext): string {
  const { domain, severity, trend, metric, durationPhrase } = ctx;
  const isMood = isMoodDomain(domain);

  if (severity === 'attention') {
    if (isMood) {
      const duration = durationPhrase ?? 'multiple consecutive weekly check-ins';
      return `Composite screening has stayed in the moderate-to-severe range across ${duration}. Clinical review recommended.`;
    }
    if (trend === 'declining') {
      const metricPart = metric ? ` ${metric.toLowerCase()}` : '';
      return `Sustained decline in${metricPart} relative to baseline. Flag for review.`;
    }
    return `${pillarDisplayName(domain)} signals flagged for review.`;
  }

  if (severity === 'fair') {
    if (isMood) {
      return 'Screening composite running above typical range this period. Worth monitoring.';
    }
    if (trend === 'declining') {
      return `${pillarDisplayName(domain)} trending below target this period. Monitor.`;
    }
    return `${pillarDisplayName(domain)} signals below target but not critical.`;
  }

  if (severity === 'good') {
    if (trend === 'improving') {
      return `${pillarDisplayName(domain)} improving vs prior period. Within target range.`;
    }
    return `${pillarDisplayName(domain)} stable within target range.`;
  }

  if (severity === 'excellent') {
    return `${pillarDisplayName(domain)} optimal this period. Continue current management.`;
  }

  return `${pillarDisplayName(domain)} — insufficient data to interpret.`;
}

